import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { analyticsService } from '@/services/api'
import { ArrowLeft, Users, CheckCircle, XCircle, Clock, Search, GraduationCap, Grid3X3, List, ChevronRight } from 'lucide-react'
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts'
import { format, parseISO, startOfMonth } from 'date-fns'
import PeriodFilter from '@/components/analytics/PeriodFilter'
import clsx from 'clsx'

function KPICard({ label, value, sub, color = 'text-gray-900', icon: Icon }) {
  return (
    <div className="card py-5">
      <div className="flex items-start gap-3">
        {Icon && <Icon size={20} className={`${color} shrink-0 mt-0.5`} />}
        <div>
          <p className={`text-3xl font-bold ${color}`}>{value ?? 0}</p>
          {sub && <p className="text-xs text-gray-400">{sub}</p>}
          <p className="text-sm text-gray-500 mt-0.5">{label}</p>
        </div>
      </div>
    </div>
  )
}

function ViewToggle({ view, onChange }) {
  return (
    <div className="flex bg-slate-100 rounded-xl p-1 gap-1">
      <button onClick={() => onChange('kanban')}
        className={clsx('p-2 rounded-lg transition-all', view === 'kanban' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-400 hover:text-slate-600')}
        title="Канбан"><Grid3X3 size={16} /></button>
      <button onClick={() => onChange('list')}
        className={clsx('p-2 rounded-lg transition-all', view === 'list' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-400 hover:text-slate-600')}
        title="Список"><List size={16} /></button>
    </div>
  )
}

export default function DirectionDetailPage() {
  const { id, dirId } = useParams()
  const navigate = useNavigate()
  const today = new Date()
  const [period, setPeriod] = useState({
    date_from: format(startOfMonth(today), 'yyyy-MM-dd'),
    date_to: format(today, 'yyyy-MM-dd'),
  })
  const [search, setSearch] = useState('')
  const [view, setView] = useState('kanban')

  const { data, isLoading } = useQuery({
    queryKey: ['direction-analytics', dirId, period],
    queryFn: () => analyticsService.directionHierarchyStats(dirId, period).then((r) => r.data),
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary-600 border-t-transparent" />
      </div>
    )
  }
  if (!data || data.error) return <div className="text-center py-20 text-gray-400">Данные не найдены</div>

  const chartData = (data.trend || []).map((d) => ({
    label: format(parseISO(String(d.date)), 'dd.MM'),
    total: d.total, on_time: d.on_time, late: d.late,
  }))

  const filteredGroups = (data.groups || []).filter(g =>
    g.name.toLowerCase().includes(search.toLowerCase())
  )

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button onClick={() => navigate(`/departments/${id}`)} className="btn-secondary btn-sm">
          <ArrowLeft size={16} /> Назад
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Направление: {data.direction?.name}</h1>
          <p className="text-sm text-gray-500">
            Кафедра: <span className="text-gray-700">{data.department?.name}</span>
          </p>
        </div>
      </div>

      <div className="card py-3">
        <PeriodFilter onChange={setPeriod} />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <KPICard label="Всего студентов" value={data.total_students} icon={Users} color="text-primary-600" />
        <KPICard label="Присутствуют сегодня" value={data.present_today} icon={CheckCircle} color="text-green-600" sub={`вовремя: ${data.on_time_today}`} />
        <KPICard label="Отсутствуют сегодня" value={data.absent_today} icon={XCircle} color="text-red-600" sub={`опоздало: ${data.late_today}`} />
        <KPICard label="% вовремя за период" value={`${data.on_time_percent ?? 0}%`} icon={Clock} color="text-blue-600" sub={`опозданий: ${data.late_percent}%`} />
      </div>

      {chartData.length > 1 && (
        <div className="card">
          <h3 className="font-semibold text-gray-800 mb-4">Тренд посещаемости</h3>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip /><Legend />
              <Area type="monotone" dataKey="total" name="Всего" stroke="#94a3b8" fill="#f1f5f9" strokeWidth={1} />
              <Area type="monotone" dataKey="on_time" name="Вовремя" stroke="#22c55e" fill="#dcfce7" strokeWidth={2} />
              <Area type="monotone" dataKey="late" name="Опоздало" stroke="#f59e0b" fill="#fef3c7" strokeWidth={1} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* GROUPS */}
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <h3 className="text-lg font-semibold text-gray-900">Группы направления ({filteredGroups.length})</h3>
          <div className="flex items-center gap-3">
            <div className="relative w-64">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input className="input pl-9 py-1.5 text-sm w-full" placeholder="Поиск группы..."
                value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <ViewToggle view={view} onChange={setView} />
          </div>
        </div>

        {view === 'kanban' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {filteredGroups.map(group => (
              <div key={group.id} onClick={() => navigate(`/analytics/groups/${group.id}`)}
                className="card p-5 cursor-pointer hover:-translate-y-1 hover:shadow-lg hover:border-primary-200 transition-all border border-transparent duration-200 group">
                <div className="flex items-start justify-between mb-3">
                  <h4 className="font-semibold text-gray-900 group-hover:text-primary-600 transition-colors line-clamp-1 flex-1">
                    Группа {group.name}
                  </h4>
                  <div className="w-8 h-8 rounded-full bg-primary-50 text-primary-600 flex items-center justify-center shrink-0"><GraduationCap size={16} /></div>
                </div>
                <div className="flex justify-between items-end border-t border-gray-100 pt-3 text-sm">
                  <div>
                    <div className="text-2xl font-bold text-gray-900">{group.total_students}</div>
                    <div className="text-xs text-gray-500">студентов</div>
                  </div>
                  <div className="text-right">
                    <div className={`text-lg font-bold ${group.on_time_percent >= 75 ? 'text-green-600' : 'text-orange-500'}`}>
                      {group.on_time_percent}%
                    </div>
                    <div className="text-xs text-gray-400">явка</div>
                  </div>
                </div>
              </div>
            ))}
            {filteredGroups.length === 0 && (
              <div className="col-span-full text-center py-10 text-gray-400 bg-white rounded-xl border border-dashed border-gray-200">
                Группы не найдены
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            {filteredGroups.map(group => (
              <div key={group.id} onClick={() => navigate(`/analytics/groups/${group.id}`)}
                className="bg-white border border-slate-200 rounded-xl px-5 py-3.5 flex items-center gap-4 hover:shadow-md transition-all cursor-pointer group">
                <div className="w-9 h-9 rounded-lg bg-violet-50 text-violet-600 border border-violet-100 flex items-center justify-center shrink-0"><GraduationCap size={18} /></div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-bold text-slate-900 truncate group-hover:text-indigo-600 transition-colors">Группа {group.name}</h4>
                </div>
                <div className="hidden sm:flex gap-4 items-center shrink-0">
                  <div className="text-center"><div className="text-sm font-black text-slate-900">{group.total_students}</div><div className="text-[8px] font-bold text-slate-400 uppercase">Студ.</div></div>
                  <div className="text-center"><div className={`text-sm font-black ${group.on_time_percent >= 75 ? 'text-green-600' : 'text-orange-500'}`}>{group.on_time_percent}%</div><div className="text-[8px] font-bold text-slate-400 uppercase">Явка</div></div>
                </div>
                <ChevronRight size={16} className="text-slate-300 group-hover:text-indigo-400 shrink-0 transition-colors" />
              </div>
            ))}
            {filteredGroups.length === 0 && (
              <div className="text-center py-10 text-gray-400 bg-white rounded-xl border border-dashed border-gray-200">
                Группы не найдены
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
